import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeft, Loader2 } from "lucide-react";
import SkillEditorForm, {
  DEFAULT_CONTENT,
  skillValidationSchema,
} from "~/components/assistant/SkillEditorForm";
import type { SkillFormData, SkillTag } from "~/components/assistant/SkillEditorForm";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import type { PartialSkillTemplate, SkillTemplate } from "~/components/assistant/types";
import { Button } from "~/components/ui/button";

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import useErrorToast from "~/contexts/error-toast";
import SkillPickerForm from "./SkillPickerForm";

interface CreateSkillInput extends SkillFormData {
  source_template_id?: string;
}

interface SkillPickerModalProps {
  api: AssistantApi;
  open: boolean;
  onClose: () => void;
  createSkill: (data: CreateSkillInput) => Promise<void>;
  onCreated: () => void;
}

const SkillPickerModal = ({
  api,
  open,
  onClose,
  createSkill,
  onCreated,
}: SkillPickerModalProps) => {
  const { showError } = useErrorToast();
  const [showEditor, setShowEditor] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SkillTemplate | null>(null);

  const {
    control,
    reset,
    handleSubmit,
    formState: { isValid },
  } = useForm<SkillFormData>({
    resolver: yupResolver(skillValidationSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      tags: [],
      content: DEFAULT_CONTENT,
    },
  });

  useEffect(() => {
    if (!open) {
      setShowEditor(false);
      setSelectedTemplate(null);
    }
  }, [open]);

  const handleAddNew = () => {
    setSelectedTemplate(null);
    reset({
      name: "",
      description: "",
      tags: [],
      content: DEFAULT_CONTENT,
    });
    setShowEditor(true);
  };

  const handleSelectTemplate = async (template: PartialSkillTemplate) => {
    setLoadingTemplate(true);
    try {
      const full = await api.getSkillTemplate(template.id);
      setSelectedTemplate(full);
      reset({
        name: full.name,
        description: full.description,
        tags: (full.tags ?? []) as SkillTag[],
        content: full.content ?? DEFAULT_CONTENT,
      });
      setShowEditor(true);
    } catch (e) {
      console.error(e);
      showError("Unable to load skill template");
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleCreate = async (data: SkillFormData) => {
    setSubmitting(true);
    try {
      await createSkill({
        ...data,
        source_template_id: selectedTemplate?.id,
      });
      onCreated();
    } catch (e) {
      console.error(e);
      showError(e instanceof Error ? e.message : "Unable to save skill");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[640px] p-0 gap-0 h-[65vh] overflow-hidden flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
          <DialogTitle className="flex items-center gap-1">
            {showEditor && (
              <button
                type="button"
                onClick={() => setShowEditor(false)}
                className="flex items-center justify-center rounded p-1.5 hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
                aria-label="Back to templates"
              >
                <ChevronLeft className="size-4" />
              </button>
            )}
            {showEditor ? "Add Skill" : "Browse Skills"}
          </DialogTitle>
        </DialogHeader>

        {loadingTemplate ? (
          <div className="flex flex-col items-center justify-center py-8 gap-4 flex-1">
            <Loader2 className="size-8 animate-spin text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading template...</p>
          </div>
        ) : showEditor ? (
          <SkillEditorForm control={control} />
        ) : (
          <SkillPickerForm
            api={api}
            open={open}
            onAddNew={handleAddNew}
            onSelectTemplate={handleSelectTemplate}
          />
        )}

        <DialogFooter className="px-6 pb-4 shrink-0">
          {showEditor && (
            <>
              <Button variant="outline" size="sm" onClick={() => setShowEditor(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit(handleCreate)}
                disabled={!isValid || submitting}
                loading={submitting}
                size="sm"
                className="min-w-[72px]"
              >
                Add
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SkillPickerModal;
